/**
 * @rynora/contracts/scim — SCIM 2.0 (RFC 7643 / 7644) wire shapes.
 *
 * Rynora's SCIM endpoints implement the minimum compliant subset:
 *   - User core schema
 *   - Group core schema
 *   - PATCH ops on the flat top-level attributes
 *   - Cursor-shaped pagination (`startIndex` + `count`)
 *
 * Anything outside this subset returns SCIM_PATCH_UNSUPPORTED so
 * IdPs fall back to PUT (full-replace) instead of PATCH.
 *
 * Stable wire-codes only — RFC 7643 fixes the property names; do NOT
 * camelCase them. Treat them as wire artefacts.
 */
import { z } from 'zod';

// SCIM Core — names and emails carry display + primary flags

export const ScimNameSchema = z.object({
  givenName: z.string().optional(),
  familyName: z.string().optional(),
  formatted: z.string().optional(),
});
export type ScimName = z.infer<typeof ScimNameSchema>;

export const ScimEmailSchema = z.object({
  value: z.string().email(),
  primary: z.boolean().optional(),
  type: z.string().optional(),
});
export type ScimEmail = z.infer<typeof ScimEmailSchema>;

// SCIM Meta — the ETag-and-version sub-object the spec requires on
// every Resource response.
export const ScimMetaSchema = z.object({
  resourceType: z.enum(['User', 'Group']),
  created: z.string(),
  lastModified: z.string(),
  location: z.string(),
  version: z.string().optional(),
});
export type ScimMeta = z.infer<typeof ScimMetaSchema>;

// SCIM User — the resource we fan IdP-managed users into.
//
// `userName` is the unique-per-org login id. `externalId` is the
// IdP-side user id; we persist both because IdPs may rename the
// userName but never the externalId.
export const ScimUserSchema = z.object({
  schemas: z.array(z.string()).default(['urn:ietf:params:scim:schemas:core:2.0:User']),
  id: z.string(),
  userName: z.string(),
  externalId: z.string().optional(),
  name: ScimNameSchema.optional(),
  displayName: z.string().optional(),
  emails: z.array(ScimEmailSchema).default([]),
  active: z.boolean().default(true),
  meta: ScimMetaSchema,
});
export type ScimUser = z.infer<typeof ScimUserSchema>;

// SCIM Group — `displayName` is the IdP-side group name, `members`
// is a list of user references. We only support the value/display
// member subset (no group-of-groups).
export const ScimGroupMemberSchema = z.object({
  value: z.string(),
  display: z.string().optional(),
});
export type ScimGroupMember = z.infer<typeof ScimGroupMemberSchema>;

export const ScimGroupSchema = z.object({
  schemas: z.array(z.string()).default(['urn:ietf:params:scim:schemas:core:2.0:Group']),
  id: z.string(),
  externalId: z.string().optional(),
  displayName: z.string(),
  members: z.array(ScimGroupMemberSchema).default([]),
  meta: ScimMetaSchema,
});
export type ScimGroup = z.infer<typeof ScimGroupSchema>;

// SCIM ListResponse — every GET /Users + /Groups uses this envelope.
export const ScimListResponseSchema = z.object({
  schemas: z.array(z.string()).default(['urn:ietf:params:scim:api:messages:2.0:ListResponse']),
  totalResults: z.number().int().nonnegative(),
  startIndex: z.number().int().positive(),
  itemsPerPage: z.number().int().nonnegative(),
  Resources: z.array(z.union([ScimUserSchema, ScimGroupSchema])),
});
export type ScimListResponse = z.infer<typeof ScimListResponseSchema>;

// SCIM Error — `status` is the HTTP code as a string, `scimType` is
// optional but encouraged (e.g. `uniqueness`, `invalidValue`).
export const ScimErrorSchema = z.object({
  schemas: z.array(z.string()).default(['urn:ietf:params:scim:api:messages:2.0:Error']),
  detail: z.string(),
  status: z.string(),
  scimType: z.string().optional(),
});
export type ScimError = z.infer<typeof ScimErrorSchema>;

// SCIM PATCH — the operations envelope. We support a flat subset
// (`add`, `remove`, `replace` on top-level attributes; `members` on
// Group). Anything else returns SCIM_PATCH_UNSUPPORTED.
export const ScimPatchOpSchema = z.object({
  op: z.enum(['add', 'remove', 'replace', 'Add', 'Remove', 'Replace']),
  path: z.string().optional(),
  value: z.unknown().optional(),
});
export type ScimPatchOp = z.infer<typeof ScimPatchOpSchema>;

export const ScimPatchOpsSchema = z.object({
  schemas: z.array(z.string()).default(['urn:ietf:params:scim:api:messages:2.0:PatchOp']),
  Operations: z.array(ScimPatchOpSchema).min(1),
});
export type ScimPatchOps = z.infer<typeof ScimPatchOpsSchema>;

// ---- Group → role mapping (Rynora-specific) ----

import { OrgRoleSchema } from './orgs';

/**
 * One row in the org-level mapping that the SSO JIT step + the
 * SCIM membership change handler both consult. When a user is in
 * multiple groups, the resolver picks the highest-precedence role.
 */
export const OrgGroupMappingSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  /** IdP-side group name (case-insensitive match). */
  idpGroupName: z.string(),
  role: OrgRoleSchema,
  /** Optional default project the user is added to as a viewer
   *  on first JIT login. NULL = no default project. */
  defaultProjectId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OrgGroupMapping = z.infer<typeof OrgGroupMappingSchema>;

export const CreateOrgGroupMappingRequestSchema = z.object({
  idpGroupName: z.string().min(1),
  role: OrgRoleSchema,
  defaultProjectId: z.string().nullable().optional(),
});
export type CreateOrgGroupMappingRequest = z.infer<typeof CreateOrgGroupMappingRequestSchema>;

export const UpdateOrgGroupMappingRequestSchema = CreateOrgGroupMappingRequestSchema.partial();
export type UpdateOrgGroupMappingRequest = z.infer<typeof UpdateOrgGroupMappingRequestSchema>;

export const ListOrgGroupMappingsResponseSchema = z.object({
  mappings: z.array(OrgGroupMappingSchema),
});
export type ListOrgGroupMappingsResponse = z.infer<typeof ListOrgGroupMappingsResponseSchema>;

// ---- SCIM bearer tokens (admin-only CRUD) ----

export const ScimTokenSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  prefix: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type ScimToken = z.infer<typeof ScimTokenSchema>;

export const CreateScimTokenRequestSchema = z.object({
  name: z.string().min(1).max(120),
});
export type CreateScimTokenRequest = z.infer<typeof CreateScimTokenRequestSchema>;

/**
 * The plaintext token is only returned on creation. The `key` field
 * is the full bearer string the IdP needs to paste into its SCIM
 * settings.
 */
export const CreatedScimTokenSchema = ScimTokenSchema.extend({
  key: z.string(),
});
export type CreatedScimToken = z.infer<typeof CreatedScimTokenSchema>;
